class MockPromise<T> {
    private _name: string;
    private _completed: boolean;
    private _errored: boolean;
    private _result: T;
    private _error: Error;
    private resolve: (value: T) => void;
    private progress: (value: number) => void;
    private reject: (error: any) => void;
    private completed: () => void;
    private _shouldRelease?: () => void;
    private _callbacks: Array<(value: T) => void>;
    private _progbacks: Array<(value: number) => void>;
    private _failbacks: Array<(error: Error) => void>;
    private _fnlybacks: Array<() => void>;

    constructor(
        name: string,
        handler: (
            resolve: (value: T) => void,
            progress: (value: number) => void,
            reject: (error: any) => void,
            completed: () => void
        ) => void,
        shouldRelease?: () => void,
    ) {
        this._name = name;
        this._completed = false;
        this._errored = false;
        this._result = null;
        this._error = null;
        this._shouldRelease = shouldRelease;
        this._callbacks = new Array<(value: T) => void>();
        this._progbacks = new Array<(value: number) => void>();
        this._failbacks = new Array<(error: Error) => void>();
        this._fnlybacks = new Array<() => void>();

        this.resolve = (value: T) => {
            this._result = value;
            this._completed = true;
            for (let callback of this._callbacks) callback(value);
            for (let callback of this._fnlybacks) callback();
        };
        this.progress = (value: number) => {
            for (let callback of this._progbacks) callback(value);
        };
        this.reject = (error: Error) => {
            this._error = error;
            this._errored = true;
            for (let callback of this._failbacks) callback(error);
            for (let callback of this._fnlybacks) callback();
        };

        handler(this.resolve, this.progress, this.reject, this.completed);
    }

    name() {
        return this._name;
    }

    attach(callback: (value: T) => void) {
        if (!!callback && !this._callbacks.includes(callback))
            this._callbacks.push(callback);
        return this;
    }

    then(callback: (value: T) => void, failback?: (error: Error) => void) {
        if (this._completed) callback(this._result);
        else if (this._errored) {
            if (!!failback) failback(this._error);
        } else {
            if (!!callback && !this._callbacks.includes(callback))
                this._callbacks.push(callback);
            if (!!failback && !this._failbacks.includes(failback))
                this._failbacks.push(failback);
        }
        return this;
    }

    partial(callback: (value: number) => void) {
        if (!!callback && !this._progbacks.includes(callback))
            this._progbacks.push(callback);
        return this;
    }

    catch(failback: (error: Error) => void) {
        if (this._errored) failback(this._error);
        else if (!!failback && !this._failbacks.includes(failback))
            this._failbacks.push(failback);
        return this;
    }

    finally(callback: () => void) {
        if (this._completed || this._errored) callback();
        else if (!!callback && !this._fnlybacks.includes(callback))
            this._fnlybacks.push(callback);
        return this;
    }

    release() {
        this._shouldRelease?.();
    }

    detach() {
        this._shouldRelease?.();
    }
}

export { MockPromise };
